import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { buildingNotesService } from '../services'
import type { BuildingNoteWithUser } from '@popety-io/types'

interface BuildingNotesContextType {
  // Data
  notes: BuildingNoteWithUser[]
  filteredNotes: BuildingNoteWithUser[]
  loading: boolean
  error: string | null

  // Search
  searchQuery: string
  setSearchQuery: (query: string) => void

  // UI State
  isAddingNote: boolean
  setIsAddingNote: (value: boolean) => void
  editingNoteId: number | null
  setEditingNoteId: (id: number | null) => void
  deleteConfirmId: number | null
  setDeleteConfirmId: (id: number | null) => void

  // Actions
  fetchNotes: (buildingId: number | string) => Promise<void>
  createNote: (content: string) => Promise<void>
  updateNote: (noteId: number, content: string) => Promise<void>
  deleteNote: (noteId: number) => Promise<void>
  refreshNotes: () => Promise<void>

  // Current building ID
  currentBuildingId: string | number | null
}

const BuildingNotesContext = createContext<
  BuildingNotesContextType | undefined
>(undefined)

export const BuildingNotesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Core state
  const [notes, setNotes] = useState<BuildingNoteWithUser[]>([])
  const [filteredNotes, setFilteredNotes] = useState<BuildingNoteWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentBuildingId, setCurrentBuildingId] = useState<
    string | number | null
  >(null)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const searchTimeout = useRef<any>(null)

  // UI state
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  // Perform search with debounce
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      performSearch(searchQuery)
    }, 300)

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
    }
  }, [searchQuery, notes])

  // Helper to extract plain text from HTML for search
  const getTextFromHtml = (html: string) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  const performSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredNotes(notes)
      return
    }

    const queryLower = query.toLowerCase()
    const filtered = notes.filter((note) => {
      // Extract plain text from HTML content for search
      const plainTextContent = getTextFromHtml(note.content)
      const searchText =
        `${note.authorName || note.userName} ${plainTextContent}`.toLowerCase()
      return searchText.includes(queryLower)
    })

    setFilteredNotes(filtered)
  }

  // Fetch notes for a specific building
  const fetchNotes = useCallback(async (buildingId: number | string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentBuildingId(buildingId)
      const notes = await buildingNotesService.getBuildingNotes(buildingId)
      setNotes(notes)
      setFilteredNotes(notes)
    } catch (err) {
      console.error('Error fetching notes:', err)
      setError('Failed to load notes')
      setNotes([])
      setFilteredNotes([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh notes for current building
  const refreshNotes = useCallback(async () => {
    if (currentBuildingId) {
      await fetchNotes(currentBuildingId)
    }
  }, [currentBuildingId, fetchNotes])

  // Create a new note
  const createNote = useCallback(
    async (content: string) => {
      if (!currentBuildingId || !content.trim()) return

      try {
        const newNote = await buildingNotesService.createBuildingNote(
          String(currentBuildingId),
          {
            content: content.trim(),
          }
        )

        const updatedNotes = [newNote, ...notes]
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setIsAddingNote(false)
      } catch (err) {
        console.error('Error creating note:', err)
        setError('Failed to create note')
        throw err
      }
    },
    [currentBuildingId, notes]
  )

  // Update an existing note
  const updateNote = useCallback(
    async (noteId: number, content: string) => {
      if (!content.trim()) return

      try {
        const updatedNote = await buildingNotesService.updateBuildingNote(
          noteId,
          {
            content: content.trim(),
          }
        )

        const updatedNotes = notes.map((note) =>
          note.id === noteId.toString() ? updatedNote : note
        )
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setEditingNoteId(null)
      } catch (err) {
        console.error('Error updating note:', err)
        setError('Failed to update note')
        throw err
      }
    },
    [notes]
  )

  // Delete a note
  const deleteNote = useCallback(
    async (noteId: number) => {
      try {
        await buildingNotesService.deleteBuildingNote(noteId)

        const updatedNotes = notes.filter(
          (note) => note.id !== noteId.toString()
        )
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setDeleteConfirmId(null)
      } catch (err) {
        console.error('Error deleting note:', err)
        setError('Failed to delete note')
        throw err
      }
    },
    [notes]
  )

  // Reset UI state when building changes
  useEffect(() => {
    setIsAddingNote(false)
    setEditingNoteId(null)
    setDeleteConfirmId(null)
    setSearchQuery('')
  }, [currentBuildingId])

  const value: BuildingNotesContextType = {
    // Data
    notes,
    filteredNotes,
    loading,
    error,

    // Search
    searchQuery,
    setSearchQuery,

    // UI State
    isAddingNote,
    setIsAddingNote,
    editingNoteId,
    setEditingNoteId,
    deleteConfirmId,
    setDeleteConfirmId,

    // Actions
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    refreshNotes,

    // Current building ID
    currentBuildingId,
  }

  return (
    <BuildingNotesContext.Provider value={value}>
      {children}
    </BuildingNotesContext.Provider>
  )
}

export const useBuildingNotes = () => {
  const context = useContext(BuildingNotesContext)
  if (context === undefined) {
    throw new Error(
      'useBuildingNotes must be used within a BuildingNotesProvider'
    )
  }
  return context
}
